/**
 * DNEEZ - Track Your Order
 * ------------------------------------------------------------------
 * Self-contained custom element <track-order>. No dependencies.
 * Scoped to the Track Order section only; does not touch cart,
 * wishlist, header or any other theme script.
 *
 * SECURITY: this file contains no credentials. Order data is fetched
 * from a same-origin Shopify App Proxy endpoint (e.g. /apps/track-order)
 * whose backend verifies order number + email/phone server-side using
 * the Admin API. See /backend-reference/README.md for the contract.
 *
 * Request  : POST {endpoint}  JSON { order_number, contact }
 * Success  : 200 { ok: true, order: {...} }        (see normalizeOrder)
 * Errors   : 400 invalid_request | 404 order_not_found |
 *            403 verification_failed | 429 rate_limited
 */
(() => {
  if (customElements.get('track-order')) return;

  const STEPS = ['confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered'];
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const ORDER_RE = /^#?[A-Za-z0-9-]{3,30}$/;
  const REQUEST_TIMEOUT = 15000;

  /* ---------- tiny DOM helper (always textContent, never innerHTML) ---------- */
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = String(text);
    return node;
  };

  const safeHttpUrl = (value) => {
    try {
      const url = new URL(String(value), window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch (e) {
      return null;
    }
  };

  class TrackOrder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-track-order-form]');
      if (!this.form) return;

      this.lookup = this.querySelector('[data-track-order-lookup]');
      this.resultEl = this.querySelector('[data-track-order-result]');
      this.alertEl = this.querySelector('[data-track-order-alert]');
      this.statusEl = this.querySelector('[data-track-order-status]');
      this.submitBtn = this.querySelector('[data-track-order-submit]');
      this.orderInput = this.form.elements.order_number;
      this.contactInput = this.form.elements.contact;
      this.endpoint = (this.dataset.endpoint || '').trim();
      this.locale = this.resolveLocale([this.dataset.locale, document.documentElement.lang, 'en-IN']);
      this.busy = false;

      try {
        this.t = JSON.parse(this.querySelector('[data-track-order-strings]').textContent);
      } catch (e) {
        this.t = { status: {}, status_desc: {} };
      }

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      [this.orderInput, this.contactInput].forEach((input) =>
        input.addEventListener('input', () => this.clearFieldError(input.name))
      );
      this.contactInput.addEventListener('input', () => this.adaptContactKeyboard());
    }

    resolveLocale(candidates) {
      for (const c of candidates) {
        if (!c) continue;
        try {
          if (Intl.DateTimeFormat.supportedLocalesOf([c]).length) return c;
        } catch (e) { /* invalid tag, try next */ }
      }
      return 'en-IN';
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.controller?.abort();
    }

    /* ---------------- validation ---------------- */
    adaptContactKeyboard() {
      const v = this.contactInput.value.trim();
      const looksLikePhone = v.length > 0 && /^[+\d\s()-]+$/.test(v);
      this.contactInput.setAttribute('inputmode', looksLikePhone ? 'tel' : 'email');
    }

    normalizeOrderNumber(value) {
      return value.trim().replace(/\s+/g, '').toUpperCase();
    }

    classifyContact(value) {
      const v = value.trim();
      if (EMAIL_RE.test(v)) return { type: 'email', value: v.toLowerCase() };
      const digits = v.replace(/[^\d]/g, '');
      if (/^[+\d\s()-]+$/.test(v) && digits.length >= 7 && digits.length <= 15) {
        return { type: 'phone', value: (v.startsWith('+') ? '+' : '') + digits };
      }
      return null;
    }

    validate() {
      let valid = true;
      const order = this.normalizeOrderNumber(this.orderInput.value);
      if (!order) {
        this.setFieldError('order_number', this.t.required_order);
        valid = false;
      } else if (!ORDER_RE.test(order)) {
        this.setFieldError('order_number', this.t.invalid_order);
        valid = false;
      }

      const rawContact = this.contactInput.value.trim();
      const contact = this.classifyContact(rawContact);
      if (!rawContact) {
        this.setFieldError('contact', this.t.required_contact);
        valid = false;
      } else if (!contact) {
        this.setFieldError('contact', this.t.invalid_contact);
        valid = false;
      }

      if (!valid) {
        (this.form.querySelector('[aria-invalid="true"]') || this.orderInput).focus();
        return null;
      }
      return { order_number: order.startsWith('#') ? order : `#${order}`, contact: contact.value, contact_type: contact.type };
    }

    setFieldError(name, message) {
      const input = this.form.elements[name];
      const msg = this.form.querySelector(`[data-error-for="${name}"]`);
      input.setAttribute('aria-invalid', 'true');
      if (msg) {
        msg.textContent = message;
        msg.hidden = false;
      }
    }

    clearFieldError(name) {
      const input = this.form.elements[name];
      const msg = this.form.querySelector(`[data-error-for="${name}"]`);
      input.removeAttribute('aria-invalid');
      if (msg) {
        msg.textContent = '';
        msg.hidden = true;
      }
    }

    showAlert(message) {
      this.alertEl.textContent = message;
      this.alertEl.hidden = false;
    }

    hideAlert() {
      this.alertEl.textContent = '';
      this.alertEl.hidden = true;
    }

    setLoading(loading) {
      this.busy = loading;
      this.submitBtn.disabled = loading;
      this.submitBtn.classList.toggle('loading', loading);
      this.submitBtn.setAttribute('aria-busy', String(loading));
      this.statusEl.textContent = loading ? this.t.checking : '';
    }

    /* ---------------- request ---------------- */
    isAllowedEndpoint(endpoint) {
      // Same-origin relative paths only (Shopify App Proxy), never an external host.
      return endpoint.startsWith('/') && !endpoint.startsWith('//');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.hideAlert();

      const payload = this.validate();
      if (!payload) return;

      if (!this.endpoint || !this.isAllowedEndpoint(this.endpoint)) {
        this.showAlert(this.t.not_connected);
        return;
      }

      this.setLoading(true);
      this.controller = new AbortController();
      const timer = setTimeout(() => this.controller.abort(), REQUEST_TIMEOUT);

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: this.controller.signal,
        });

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }

        if (response.ok && data && data.ok && data.order) {
          this.renderResult(this.normalizeOrder(data.order));
          return;
        }

        const code = (data && data.error) || (response.status === 404 ? 'order_not_found'
          : response.status === 403 ? 'verification_failed'
          : response.status === 429 ? 'rate_limited' : 'generic');
        this.showAlert(this.t[code] || this.t.generic);
      } catch (error) {
        this.showAlert(this.t.generic);
      } finally {
        clearTimeout(timer);
        this.setLoading(false);
      }
    }

    /* ---------------- data shaping ---------------- */
    normalizeOrder(o) {
      const stage = STEPS.includes(o.stage) || o.stage === 'cancelled' ? o.stage : 'confirmed';
      return {
        name: String(o.name || ''),
        createdAt: o.created_at || null,
        paymentStatus: String(o.payment_status || ''),
        stage,
        estimatedDelivery: o.estimated_delivery || null, // { from, to } ISO dates
        subtotal: o.subtotal || '',
        shipping: o.shipping || '',
        total: o.total || '',
        lineItems: Array.isArray(o.line_items) ? o.line_items.slice(0, 50) : [],
        shipments: Array.isArray(o.shipments) ? o.shipments.slice(0, 10) : [],
      };
    }

    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      try {
        return new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: 'long', year: 'numeric' }).format(d);
      } catch (e) {
        return d.toDateString();
      }
    }

    formatRange(range) {
      if (!range) return '';
      const from = range.from ? new Date(range.from) : null;
      const to = range.to ? new Date(range.to) : null;
      if (from && to && !isNaN(from.getTime()) && !isNaN(to.getTime()) && +from !== +to) {
        try {
          const fmt = new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: 'long', year: 'numeric' });
          if (fmt.formatRange) return fmt.formatRange(from, to);
        } catch (e) { /* fall through */ }
        return `${this.formatDate(range.from)} \u2013 ${this.formatDate(range.to)}`;
      }
      return this.formatDate(range.from || range.to);
    }

    /* ---------------- rendering ---------------- */
    renderResult(order) {
      const root = this.resultEl;
      root.replaceChildren();

      const card = el('div', 'track-order__card');

      // Header
      const head = el('div', 'track-order__result-head');
      head.append(el('h2', 'track-order__result-title heading-color heading-letter-spacing h4 m-0', `${this.t.order_title} ${order.name}`));
      head.append(el('span', `track-order__badge track-order__badge--${order.stage}`, this.t.status[order.stage]));
      card.append(head);

      // Facts
      const facts = el('dl', 'track-order__facts track-order__facts--grid');
      const addFact = (label, value) => {
        if (!value) return;
        const row = el('div');
        row.append(el('dt', '', label), el('dd', '', value));
        facts.append(row);
      };
      addFact(this.t.order_date, this.formatDate(order.createdAt));
      addFact(this.t.payment_status, order.paymentStatus);
      addFact(this.t.order_status, this.t.status[order.stage]);
      addFact(this.t.estimated_delivery, this.formatRange(order.estimatedDelivery));
      card.append(facts);

      // Timeline
      if (order.stage === 'cancelled') {
        card.append(el('p', 'track-order__note track-order__note--warning', this.t.cancelled));
      } else {
        card.append(this.renderTimeline(order.stage));
      }

      // Shipments
      card.append(this.renderShipments(order));

      // Products
      if (order.lineItems.length) card.append(this.renderProducts(order));

      root.append(card);

      const again = el('button', 'btn btn-outline track-order__again', this.t.track_another);
      again.type = 'button';
      again.addEventListener('click', () => this.reset());
      root.append(again);

      this.lookup.hidden = true;
      root.hidden = false;
      root.focus({ preventScroll: true });
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderTimeline(stage) {
      const current = STEPS.indexOf(stage);
      const list = el('ol', 'track-order__timeline list-none p-0');
      list.setAttribute('aria-label', this.t.order_status);
      STEPS.forEach((step, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'upcoming';
        // The final step is complete once reached.
        const finalState = step === 'delivered' && i === current ? 'done' : state;
        const li = el('li', `track-order__step is-${finalState}`);
        if (i === current) li.setAttribute('aria-current', 'step');
        const dot = el('span', 'track-order__dot');
        dot.setAttribute('aria-hidden', 'true');
        const body = el('div', 'track-order__step-body');
        body.append(el('span', 'track-order__step-title', this.t.status[step]));
        if (i <= current && this.t.status_desc[step]) {
          body.append(el('span', 'track-order__step-desc', this.t.status_desc[step]));
        }
        li.append(dot, body);
        list.append(li);
      });
      return list;
    }

    renderShipments(order) {
      const wrap = el('div', 'track-order__block');
      wrap.append(el('h3', 'track-order__block-title heading-color', this.t.shipment));

      const shipments = order.shipments.filter((s) => s && (s.tracking_number || s.courier));
      if (!shipments.length) {
        const msg = ['shipped', 'out_for_delivery', 'delivered'].includes(order.stage)
          ? this.t.tracking_unavailable
          : this.t.no_tracking;
        if (order.stage !== 'cancelled') wrap.append(el('p', 'track-order__note', msg));
        return wrap;
      }

      shipments.forEach((s) => {
        const box = el('div', 'track-order__shipment');
        const facts = el('dl', 'track-order__facts');
        const add = (label, value, cls = '') => {
          if (!value) return;
          const row = el('div');
          row.append(el('dt', '', label), el('dd', cls, value));
          facts.append(row);
        };
        add(this.t.courier, s.courier);
        add(this.t.tracking_number, s.tracking_number, 'break-all');
        add(this.t.estimated_delivery, this.formatDate(s.estimated_delivery));
        box.append(facts);

        // Only real carrier URLs returned by Shopify are linked, never constructed here.
        const url = s.tracking_url ? safeHttpUrl(s.tracking_url) : null;
        if (url) {
          const a = el('a', 'btn btn-primary track-order__shipment-btn', this.t.track_shipment);
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          box.append(a);
        }
        wrap.append(box);
      });
      return wrap;
    }

    renderProducts(order) {
      const wrap = el('div', 'track-order__block');
      wrap.append(el('h3', 'track-order__block-title heading-color', this.t.products));
      const list = el('ul', 'track-order__items list-none p-0 m-0');

      order.lineItems.forEach((item) => {
        const li = el('li', 'track-order__item');
        const media = el('div', 'track-order__item-media');
        const src = item.image ? safeHttpUrl(item.image) : null;
        if (src) {
          const img = el('img');
          img.src = src;
          img.alt = String(item.title || '');
          img.loading = 'lazy';
          img.width = 120;
          img.height = 160;
          media.append(img);
        }
        const info = el('div', 'track-order__item-info');
        info.append(el('p', 'track-order__item-title heading-color m-0', item.title));
        const meta = [];
        if (item.size) meta.push(`${this.t.size}: ${item.size}`);
        else if (item.variant_title) meta.push(item.variant_title);
        meta.push(`${this.t.quantity}: ${Number(item.quantity) || 1}`);
        info.append(el('p', 'track-order__meta m-0', meta.join(' \u00b7 ')));
        const price = el('p', 'track-order__item-price heading-color m-0', item.price);
        li.append(media, info, price);
        list.append(li);
      });
      wrap.append(list);

      const totals = el('dl', 'track-order__totals');
      const addTotal = (label, value, cls) => {
        if (!value) return;
        const row = el('div', cls);
        row.append(el('dt', '', label), el('dd', '', value));
        totals.append(row);
      };
      addTotal(this.t.subtotal, order.subtotal);
      addTotal(this.t.shipping, order.shipping);
      addTotal(this.t.total, order.total, 'is-total heading-color');
      wrap.append(totals);
      return wrap;
    }

    reset() {
      this.resultEl.hidden = true;
      this.resultEl.replaceChildren();
      this.lookup.hidden = false;
      this.hideAlert();
      this.form.reset();
      this.orderInput.focus();
    }
  }

  customElements.define('track-order', TrackOrder);
})();
